
/*
 * VNCcontact+ : A new level of contact management
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import * as fromTrashContacts from "../actions/trash-contacts.action";
import { createEntityAdapter, EntityAdapter, EntityState } from "@ngrx/entity";
import { Action } from "../../../actions";
import { Contact } from "src/app/common/models";

export interface ContactTrashState extends EntityState<Contact> {
  isLoaded: boolean;
  isLoading: boolean;

  isNextPageLoading: boolean;
  isNextPageLoaded: boolean;
  currentOffset: number;
  isMoreContacts: boolean;

  selectedContactsIds: number[];
  isSearchMode: boolean;
  queryParams: any;
}

export const contactTrashAdapter: EntityAdapter<Contact> = createEntityAdapter<Contact>({
  selectId: (contact: Contact) => contact.id
});

export function sortByFirstCharacter(contact1: Contact, contact2: Contact): number {
  return contact1.fullName.charCodeAt(0) - contact2.fullName.charCodeAt(0);
}

export const initialState: ContactTrashState = contactTrashAdapter.getInitialState({
  isLoading: false,
  isLoaded: false,

  isNextPageLoading: false,
  isNextPageLoaded: false,
  currentOffset: 0,
  isMoreContacts: false,

  selectedContactsIds: [],
  sortBy: null,
  isSearchMode: false,
  queryParams : null
});

export function trashContactReducer(
  state = initialState,
  action: Action
): ContactTrashState {
  switch (action.type) {
    case fromTrashContacts.TrashContactsAcTypes.LOAD_TRASH_CONTACTS: {
      return {
        ...state,
        isLoading: true,
      };
    }

    case fromTrashContacts.TrashContactsAcTypes.LOAD_TRASH_CONTACTS_SUCCESS: {
      return contactTrashAdapter.addAll(action.payload.contact, {
        ...state,
        isLoading: false,
        isLoaded: true,
        currentOffset: action.payload.currOffset,
        isMoreContacts: action.payload.isMoreContacts
      });
    }

    case fromTrashContacts.TrashContactsAcTypes.LOAD_TRASH_CONTACTS_FAIL: {
      return {
        ...state,
        isLoading: false,
        isLoaded: false,
      };
    }

    case fromTrashContacts.TrashContactsAcTypes.NEXT_LOAD_TRASH_CONTACTS: {
      return {
        ...state,
        isNextPageLoading: true,
        isLoading: true
      };
    }

    case fromTrashContacts.TrashContactsAcTypes.NEXT_LOAD_TRASH_CONTACTS_SUCCESS: {
      const newState = contactTrashAdapter.addMany(action.payload.contacts, {
        ...state,
        isNextPageLoading: false,
        isNextPageLoaded: true,
        isLoading: false,
        isLoaded: true,
        currentOffset: action.payload.currOffset,
        isMoreContacts: action.payload.isMoreContacts
      });

      const changes = action.payload.contacts.map(contact => {
        return {id: contact.id, changes: contact };
      });
      return contactTrashAdapter.updateMany(changes, newState);
    }

    case fromTrashContacts.TrashContactsAcTypes.EMPTY_TRASH_CONTACTS_SUCCESS: {
      const newState = contactTrashAdapter.removeAll( {
        ...state,
        isLoading: false,
        isLoaded: true,
        currentOffset: 0,
        isMoreContacts: false
      });
      return newState;
    }

    case fromTrashContacts.TrashContactsAcTypes.NEXT_LOAD_TRASH_CONTACTS_FAIL: {
      return {
        ...state,
        isNextPageLoading: false,
        isNextPageLoaded: false,
        isLoading: false,
        isLoaded: false,
      };
    }

    case fromTrashContacts.TrashContactsAcTypes.UPDATE_TRASH_CONTACT: {
      return {
        ...state,
        isLoading: true,
      };
    }

    case fromTrashContacts.TrashContactsAcTypes.UPDATE_TRASH_CONTACT_SUCCESS: {
      return contactTrashAdapter.updateOne(action.payload, {
        ...state,
        isLoading: false,
        isLoaded: true,
      });
    }

    case fromTrashContacts.TrashContactsAcTypes.UPDATE_TRASH_CONTACT_FAIL: {
      return {
        ...state,
        isLoading: false,
        isLoaded: false,
      };
    }

    case fromTrashContacts.TrashContactsAcTypes.CREATE_TRASH_CONTACT: {
      return {
        ...state,
        isLoading: true,
      };
    }

    case fromTrashContacts.TrashContactsAcTypes.CREATE_TRASH_CONTACT_SUCCESS: {
      return contactTrashAdapter.addOne(action.payload, {
        ...state,
        isLoading: false,
        isLoaded: true,
      });
    }

    case fromTrashContacts.TrashContactsAcTypes.CREATE_TRASH_CONTACT_FAIL: {
      return {
        ...state,
        isLoading: false,
        isLoaded: false,
      };
    }

    case fromTrashContacts.TrashContactsAcTypes.REMOVE_TRASH_CONTACT: {
      return {
        ...state,
        isLoading: true,
      };
    }

    case fromTrashContacts.TrashContactsAcTypes.REMOVE_TRASH_CONTACT_SUCCESS: {
      return contactTrashAdapter.removeOne(action.payload, {
        ...state,
        isLoading: false,
        isLoaded: true,
      });
    }

    case fromTrashContacts.TrashContactsAcTypes.REMOVE_MANY_TRASH_CONTACT_SUCCESS: {
      return contactTrashAdapter.removeMany(action.payload, {
        ...state,
        isLoading: false,
        isLoaded: true,
      });
    }

    case fromTrashContacts.TrashContactsAcTypes.REMOVE_TRASH_CONTACT_FAIL: {
      return {
        ...state,
        isLoading: false,
        isLoaded: false,
      };
    }

    case fromTrashContacts.TrashContactsAcTypes.RESTORE_TRASH_CONTACT: {
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
      };
    }

    case fromTrashContacts.TrashContactsAcTypes.RESTORE_TRASH_CONTACT_SUCCESS: {
      return contactTrashAdapter.addMany(action.payload, {
        ...state,
        isLoading: false,
        isLoaded: true,
      });
    }

    case fromTrashContacts.TrashContactsAcTypes.RESTORE_TRASH_CONTACT_FAIL: {
      return {
        ...state,
        isLoading: false,
        isLoaded: false,
      };
    }

    case fromTrashContacts.TrashContactsAcTypes.SEARCH_TOGGLE: {
      return  {
        ...state,
        isSearchMode: action.payload,
      };
    }

    case fromTrashContacts.TrashContactsAcTypes.START_TRASH_LOADING: {
      return {
        ...state,
        isLoading: true
      };
    }

    case fromTrashContacts.TrashContactsAcTypes.STOP_TRASH_LOADING: {
      return {
        ...state,
        isLoading: false,
      };
    }

    case fromTrashContacts.TrashContactsAcTypes.STORE_TRASH_SEARCHPARAMS: {
      return {
        ...state,
        queryParams: action.payload,
      };
    }

    case fromTrashContacts.TrashContactsAcTypes.RESTORE_SAVED_STATE: {
      const savedState = action.payload.contactState;
      return savedState ? { ...state, ...savedState } : state;
    }

    case fromTrashContacts.TrashContactsAcTypes.UPDATE_MANY_TRASH_CONTACT_SUCCESS: {
      return contactTrashAdapter.updateMany(action.payload, {
        ...state,
        isLoading: false,
        isLoaded: true,
      });
    }

  }
  return state;
}

export const _getIsSearchMode = (state: ContactTrashState) => state.isSearchMode;
export const _getSelectedTrashContactsIds = (state: ContactTrashState) => state.selectedContactsIds;
export const _getIsTrashContactsLoading = (state: ContactTrashState) => state.isLoading;
export const _getIsTrashContactsLoaded = (state: ContactTrashState) => state.isLoaded;
export const _getCurrentTrashOffset = (state: ContactTrashState) => state.currentOffset;
export const _getIsTrashNextPageLoading = (state: ContactTrashState) => state.isNextPageLoading;
export const _getIsTrashNextPageLoaded = (state: ContactTrashState) => state.isNextPageLoaded;
export const _getTrashSearchParams = (state: ContactTrashState) => state.queryParams;
export const _getIsTrashMoreContacts = (state: ContactTrashState) => state.isMoreContacts;
